import { type ReactNode, useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { useApi } from "../app/ApiProvider";
import { useAuth } from "../app/AuthProvider";
import {
	type Area,
	END_USER_NAV,
	landingFor,
	type NavGroup,
	type NavItem,
	SYSTEM_ADMIN_NAV,
	TENANT_ADMIN_NAV,
} from "../app/navigation";
import type { ScreenModule } from "../app/screen-module";
import { cx } from "../ui/primitives";
import { BackendModeBadge } from "./BackendModeBadge";
import { FeedbackModal } from "./FeedbackModal";
import "./shell.css";
import { useTheme } from "./theme";

/** 発注者が確認するまで左サイドメニューに付ける。外す指示があるまで残す。 */
const PENDING_REVIEW_MARK = "[未確認]";
const SIDE_KEY = "agentry:app-side";

const AREA_META: Record<
	Exclude<Area, "public">,
	{ label: string; nav: NavGroup[]; homeTo: string }
> = {
	"end-user": { label: "ワークスペース", nav: END_USER_NAV, homeTo: "/app/workspace" },
	"tenant-admin": { label: "組織管理", nav: TENANT_ADMIN_NAV, homeTo: "/admin" },
	"system-admin": { label: "製品管理", nav: SYSTEM_ADMIN_NAV, homeTo: "/system" },
};

function loadCollapsed(): boolean {
	try {
		return window.sessionStorage.getItem(SIDE_KEY) === "collapsed";
	} catch {
		return false;
	}
}

export function AppShell({
	area,
	backing,
	children,
}: {
	area: Exclude<Area, "public">;
	backing: ScreenModule["backing"];
	children: ReactNode;
}) {
	const meta = AREA_META[area];
	const { state, logout } = useAuth();
	const { contract } = useApi();
	const location = useLocation();
	const navigate = useNavigate();
	const current = currentNavItem(meta.nav, location.pathname);
	const isWorkspaceVisual = /^\/app\/workspace\/[^/]+$/.test(location.pathname);
	const workspaceName =
		state.status === "authenticated" ? (state.project?.name ?? undefined) : undefined;

	const [collapsed, setCollapsed] = useState(loadCollapsed);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [feedbackOpen, setFeedbackOpen] = useState(false);

	useEffect(() => {
		try {
			window.sessionStorage.setItem(SIDE_KEY, collapsed ? "collapsed" : "open");
		} catch {
			/* ignore */
		}
	}, [collapsed]);

	useEffect(() => {
		setMobileOpen(false);
	}, [location.pathname]);

	const versionLabel =
		contract.status === "ok"
			? `v${contract.contractVersion} - Zerovia`
			: contract.status === "checking"
				? "接続確認中…"
				: "v0.0.0 - local";

	const onLogout = async () => {
		setMobileOpen(false);
		await logout();
		navigate("/login");
	};

	const onUserSettings = () => {
		setMobileOpen(false);
		navigate("/app/settings");
	};

	return (
		<div className="ag-shell">
			{collapsed ? (
				<aside className="ag-side ag-side-desktop" data-collapsed="true">
					<button
						type="button"
						className="ag-icon-button"
						onClick={() => setCollapsed(false)}
						title="メニューを開く"
						aria-label="メニューを開く"
					>
						»
					</button>
				</aside>
			) : (
				<ShellSidebar
					area={area}
					className="ag-side-desktop"
					onToggle={() => setCollapsed(true)}
					onLogout={onLogout}
					onUserSettings={onUserSettings}
				/>
			)}

			{mobileOpen ? (
				<>
					<button
						type="button"
						className="ag-drawer-scrim"
						aria-label="メニューを閉じる"
						onClick={() => setMobileOpen(false)}
					/>
					<ShellSidebar
						area={area}
						className="ag-side-drawer"
						onToggle={() => setMobileOpen(false)}
						onNavigate={() => setMobileOpen(false)}
						onLogout={onLogout}
						onUserSettings={onUserSettings}
					/>
				</>
			) : null}

			<div className="ag-body">
				<header className="ag-main-header">
					<div className="ag-main-header-left">
						<button
							type="button"
							className="ag-icon-button ag-mobile-only"
							aria-label="メニューを開く"
							onClick={() => setMobileOpen(true)}
						>
							☰
						</button>
						<Link to={meta.homeTo} className="ag-home-link" title="HOMEへ">
							⌂ HOMEへ
						</Link>
						{isWorkspaceVisual ? (
							<>
								<Link to="/app/workspace" className="ag-home-link">
									一覧へ
								</Link>
								<p className="ag-main-title">ワークスペース / {workspaceName || "…"}</p>
							</>
						) : (
							<p className="ag-main-title">{current?.label ?? meta.label}</p>
						)}
					</div>
					<div className="flex items-center gap-2">
						<ProjectSwitcher />
						<BackendModeBadge />
					</div>
				</header>

				<div className="ag-sub-header">
					<span className="ag-ea-badge">Early Access</span>
					<span>
						Agentry は Early Access 期間中です。AI Agent
						の精度を調整しています。うまくいかなかった点は下部「ご意見はこちら」へ。
					</span>
				</div>

				<div className={cx("ag-content", isWorkspaceVisual && "is-flush")}>
					<div className={cx("ag-content-inner", isWorkspaceVisual && "is-flush")}>
						<MockOnlyScreenNotice backing={backing} />
						{children}
					</div>
				</div>

				<footer className="ag-footer">
					<span>© 2026 Agentry</span>
					<div className="ag-footer-end">
						<span title={versionLabel}>{versionLabel}</span>
						<button
							type="button"
							className="ag-footer-feedback"
							onClick={() => setFeedbackOpen(true)}
						>
							ご意見はこちら
						</button>
					</div>
				</footer>
			</div>

			<FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
		</div>
	);
}

function ShellSidebar({
	area,
	className,
	onToggle,
	onNavigate,
	onLogout,
	onUserSettings,
}: {
	area: Exclude<Area, "public">;
	className?: string;
	onToggle: () => void;
	onNavigate?: () => void;
	onLogout: () => Promise<void>;
	onUserSettings: () => void;
}) {
	const meta = AREA_META[area];
	const { session, role } = useAuth();
	const { themeId, setThemeId, themes } = useTheme();
	const [systemOpen, setSystemOpen] = useState(false);
	const [logoutPending, setLogoutPending] = useState(false);

	return (
		<aside className={cx("ag-side", className)} data-collapsed="false">
			<div className="ag-side-head">
				<Link to={landingFor(role)} className="ag-side-logo" onClick={onNavigate}>
					<span className="ag-side-logo-mark" aria-hidden="true">
						A
					</span>
					<span>Agentry</span>
				</Link>
			</div>

			<div className="ag-side-row">
				<div className="ag-theme-select">
					<select
						aria-label="カラーテーマ"
						value={themeId}
						onChange={(event) => setThemeId(event.target.value as typeof themeId)}
					>
						{themes.map((theme) => (
							<option key={theme.id} value={theme.id}>
								{theme.label}
							</option>
						))}
					</select>
				</div>
				<button
					type="button"
					className="ag-icon-button"
					onClick={onToggle}
					title="メニューを閉じる"
					aria-label="メニューを閉じる"
				>
					«
				</button>
			</div>

			{session ? (
				<div className="ag-tenant">
					<p className="ag-tenant-static" title={session.tenant.name}>
						{session.tenant.name}
					</p>
					<span className="ag-tenant-role">所属中</span>
				</div>
			) : null}

			<nav className="ag-nav" aria-label={meta.label}>
				<NavigationGroups groups={meta.nav} onNavigate={onNavigate} />
				<AreaSwitcher current={area} onNavigate={onNavigate} />
			</nav>

			<div className="ag-side-user">
				{session ? (
					<>
						<span className="ag-avatar" aria-hidden="true">
							{(session.user.email[0] ?? "?").toUpperCase()}
						</span>
						<span className="ag-side-user-body">
							<span className="ag-side-user-role">ログイン中</span>
							<span className="ag-side-user-email" title={session.user.email}>
								{session.user.email}
							</span>
						</span>
					</>
				) : null}
				<div className="ag-system">
					<button
						type="button"
						className="ag-icon-button"
						onClick={() => setSystemOpen((value) => !value)}
						title="設定"
						aria-label="設定"
						aria-expanded={systemOpen}
					>
						⚙
					</button>
					{systemOpen ? (
						<>
							<button
								type="button"
								className="ag-system-scrim"
								aria-label="メニューを閉じる"
								onClick={() => setSystemOpen(false)}
							/>
							<div className="ag-system-menu" role="menu">
								<button
									type="button"
									className="ag-system-item"
									role="menuitem"
									onClick={() => {
										setSystemOpen(false);
										onUserSettings();
									}}
								>
									ユーザー設定
								</button>
								<button
									type="button"
									className="ag-system-item"
									role="menuitem"
									disabled={logoutPending}
									onClick={async () => {
										setLogoutPending(true);
										setSystemOpen(false);
										try {
											await onLogout();
										} finally {
											setLogoutPending(false);
										}
									}}
								>
									{logoutPending ? "ログアウト中…" : "ログアウト"}
								</button>
							</div>
						</>
					) : null}
				</div>
			</div>
		</aside>
	);
}

function PendingReviewName({ name }: { name: string }) {
	return (
		<span className="flex min-w-0 items-center gap-1.5">
			<span className="truncate">{name}</span>
			<span className="shrink-0 text-[10.5px] font-bold tracking-wide opacity-70">
				{PENDING_REVIEW_MARK}
			</span>
		</span>
	);
}

function NavigationGroups({ groups, onNavigate }: { groups: NavGroup[]; onNavigate?: () => void }) {
	return (
		<>
			{groups.map((group) => (
				<div key={group.title || "__primary__"}>
					{group.title ? <p className="ag-nav-section-title">{group.title}</p> : null}
					<ul className="space-y-0.5">
						{group.items.map((item) => (
							<li key={item.to}>
								<NavLink
									to={item.to}
									end={item.exact}
									onClick={onNavigate}
									className={({ isActive }) => cx("ag-nav-item", isActive && "is-active")}
								>
									{item.icon ? <NavIcon name={item.icon} /> : null}
									<PendingReviewName name={item.label} />
								</NavLink>
							</li>
						))}
					</ul>
				</div>
			))}
		</>
	);
}

function NavIcon({ name }: { name: NonNullable<NavItem["icon"]> }) {
	const common = "size-4 shrink-0 opacity-90";
	if (name === "workspace") {
		return (
			<svg viewBox="0 0 24 24" className={common} fill="none" aria-hidden="true">
				<path
					d="M4 7.5A2.5 2.5 0 0 1 6.5 5h11A2.5 2.5 0 0 1 20 7.5v9A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5v-9Z"
					stroke="currentColor"
					strokeWidth="1.8"
				/>
				<path d="M4 10h16M10 10v9" stroke="currentColor" strokeWidth="1.8" />
			</svg>
		);
	}
	return (
		<svg viewBox="0 0 24 24" className={common} fill="none" aria-hidden="true">
			<path
				d="M3.5 8.5 12 4l8.5 4.5v7L12 20l-8.5-4.5v-7Z"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinejoin="round"
			/>
			<path d="M3.5 8.5 12 13l8.5-4.5M12 13v7" stroke="currentColor" strokeWidth="1.8" />
		</svg>
	);
}

function AreaSwitcher({
	current,
	onNavigate,
}: {
	current: Exclude<Area, "public">;
	onNavigate?: () => void;
}) {
	const { role } = useAuth();
	const targets: Array<{ area: Exclude<Area, "public">; to: string; label: string }> = [];
	if (role === "tenant_member" || role === "tenant_admin" || role === "platform_admin") {
		targets.push({ area: "end-user", to: "/app/workspace", label: "ワークスペース" });
	}
	if (role === "tenant_admin" || role === "platform_admin") {
		targets.push({ area: "tenant-admin", to: "/admin", label: "組織管理" });
	}
	if (role === "platform_admin" || role === "platform_viewer") {
		targets.push({ area: "system-admin", to: "/system", label: "製品管理" });
	}
	const others = targets.filter((target) => target.area !== current);
	if (others.length === 0) return null;

	return (
		<div>
			<p className="ag-nav-section-title">表示を切り替える</p>
			<ul className="space-y-0.5">
				{others.map((target) => (
					<li key={target.to}>
						<Link to={target.to} onClick={onNavigate} className="ag-nav-item">
							<span className="flex min-w-0 items-center">
								<PendingReviewName name={target.label} /> へ
							</span>
							<span aria-hidden="true">→</span>
						</Link>
					</li>
				))}
			</ul>
		</div>
	);
}

function ProjectSwitcher() {
	const { client } = useApi();
	const { session, scope, setProject } = useAuth();
	const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
	useEffect(() => {
		if (!session) return;
		let active = true;
		client.listOrganizationProjects(session.tenant.id).then((items) => {
			if (active) setProjects(items);
		});
		return () => {
			active = false;
		};
	}, [client, session]);
	if (!session || projects.length < 2) return null;
	return (
		<label className="text-[11px] font-bold text-ink-500">
			<span className="sr-only">プロジェクト</span>
			<select
				aria-label="プロジェクトを切り替える"
				className="rounded-lg border border-ink-200 bg-white/80 px-2 py-1.5 text-[13px] text-ink-800"
				value={scope?.projectId ?? ""}
				onChange={(event) => {
					const selected = projects.find((item) => item.id === event.target.value);
					if (selected) setProject(selected.id, selected.name);
				}}
			>
				{projects.map((project) => (
					<option key={project.id} value={project.id}>
						{project.name}
					</option>
				))}
			</select>
		</label>
	);
}

function currentNavItem(groups: NavGroup[], pathname: string) {
	return groups
		.flatMap((group) => group.items)
		.filter((item) => (item.exact ? pathname === item.to : pathname.startsWith(item.to)))
		.sort((left, right) => right.to.length - left.to.length)[0];
}

function MockOnlyScreenNotice({ backing }: { backing: ScreenModule["backing"] }) {
	const { mode } = useApi();
	if (mode !== "real" || backing !== "MOCK_ONLY") return null;
	return (
		<div className="mb-5 rounded-xl border border-warn-500/40 bg-warn-100 px-4 py-3 shadow-state">
			<p className="text-[13px] font-bold text-ink-900">この画面はプレビュー表示です</p>
			<p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-700">
				この機能はZeroviaとの接続準備中です。表示内容はサンプルで、ここでの変更は保存されません。
			</p>
		</div>
	);
}
